<template>
    <div>
        <NavbarOne />

        <div
            class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70"
            :style="{ backgroundImage: 'url(' + breadcumbBg + ')' }"
        >
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Request a Quotation</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4 flex-wrap">
                    <li><router-link to="/">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">Request a Quotation</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100">
            <div class="container-fluid">
                <div class="rq max-w-[860px] mx-auto">

                    <!-- ===== Success ===== -->
                    <div v-if="success" class="rq-card rq-success" data-aos="fade-up">
                        <div class="rq-success__icon">
                            <svg width="26" height="20" viewBox="0 0 20 15" fill="none"><path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z" fill="currentColor"/></svg>
                        </div>
                        <h3 class="rq-success__title">Your request is in</h3>
                        <p class="rq-success__ref">Reference <strong>{{ success.reference }}</strong></p>
                        <p class="rq-success__text">
                            <template v-if="success.customer_mail_sent">We emailed a copy to <strong>{{ success.email }}</strong>. </template>
                            Our team reads every request and usually comes back within one working day with a price and a build time.
                            You will get the invoice by email, with a link to accept it or ask for changes.
                        </p>
                        <div class="rq-actions rq-actions--center">
                            <router-link v-if="success.has_account" to="/my-requests" class="btn btn-solid" data-text="Track my request"><span>Track my request</span></router-link>
                            <router-link v-else to="/shop" class="btn btn-solid" data-text="Back to the shop"><span>Back to the shop</span></router-link>
                            <button type="button" class="btn btn-outline" data-text="Send another request" @click="startOver"><span>Send another request</span></button>
                        </div>
                        <p v-if="!success.has_account" class="rq-success__hint">
                            Want to follow it online? <router-link to="/register" class="text-primary underline">Create an account</router-link> with the same email and it appears under <em>My Requests</em>.
                        </p>
                    </div>

                    <!-- ===== Form ===== -->
                    <div v-else class="rq-card" data-aos="fade-up">
                        <!-- Progress -->
                        <ol class="rq-steps" aria-label="Progress">
                            <li
                                v-for="(label, index) in STEP_LABELS"
                                :key="label"
                                class="rq-steps__item"
                                :class="{ 'is-active': step === index + 1, 'is-done': step > index + 1, 'has-error': stepHasError(index + 1) }"
                            >
                                <button type="button" class="rq-steps__btn" :disabled="index + 1 > furthestStep" @click="goTo(index + 1)">
                                    <span class="rq-steps__num">
                                        <svg v-if="step > index + 1" width="11" height="9" viewBox="0 0 20 15" fill="none"><path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z" fill="currentColor"/></svg>
                                        <template v-else>{{ index + 1 }}</template>
                                    </span>
                                    <span class="rq-steps__label">{{ label }}</span>
                                </button>
                            </li>
                        </ol>

                        <!-- Linked product -->
                        <div v-if="linkedProduct" class="rq-product">
                            <img v-if="linkedProduct.image" :src="linkedProduct.image" :alt="linkedProduct.name" class="rq-product__img" />
                            <div class="rq-product__body">
                                <div class="rq-product__eyebrow">Quote for a catalog piece</div>
                                <div class="rq-product__name">{{ linkedProduct.name }} <span v-if="linkedProduct.sku" class="rq-product__sku">{{ linkedProduct.sku }}</span></div>
                                <div v-if="linkedProduct.selected_summary" class="rq-product__opts">{{ linkedProduct.selected_summary }}</div>
                            </div>
                            <button type="button" class="rq-product__remove" title="Start from scratch instead" @click="unlinkProduct">×</button>
                        </div>

                        <div v-if="restoredDraft" class="rq-note">
                            We restored what you typed earlier.
                            <button type="button" class="rq-note__link" @click="clearDraft">Start fresh</button>
                        </div>

                        <!-- ===== Step 1: What do you need ===== -->
                        <section v-show="step === 1" class="rq-step">
                            <h3 class="rq-step__title">What do you need?</h3>
                            <p class="rq-step__intro">A couple of lines is enough — we will call you for the details.</p>

                            <div class="rq-field">
                                <label class="rq-label">What kind of piece is it?</label>
                                <div class="rq-types">
                                    <button
                                        v-for="type in ITEM_TYPES"
                                        :key="type.value"
                                        type="button"
                                        class="rq-type"
                                        :class="{ 'is-selected': form.item_type === type.value }"
                                        @click="selectItemType(type.value)"
                                    >
                                        <span class="rq-type__icon" v-html="type.icon"></span>
                                        <span>{{ type.label }}</span>
                                    </button>
                                </div>
                                <FieldError :message="fieldError('item_type')" />
                            </div>

                            <div class="rq-grid">
                                <div class="rq-field rq-field--grow">
                                    <label class="rq-label" for="rq-title">Give it a short name <span class="rq-required">(required)</span></label>
                                    <input id="rq-title" v-model="form.title" class="rq-input" type="text" placeholder="e.g. L-shaped sofa for the living room" @input="clearError('title')">
                                    <FieldError :message="fieldError('title')" />
                                </div>
                                <div class="rq-field rq-field--qty">
                                    <label class="rq-label" for="rq-qty">How many?</label>
                                    <div class="rq-qty">
                                        <button type="button" class="rq-qty__btn" aria-label="Fewer" @click="form.quantity = Math.max(1, Number(form.quantity || 1) - 1)">−</button>
                                        <input id="rq-qty" v-model.number="form.quantity" class="rq-qty__input" type="number" min="1" inputmode="numeric">
                                        <button type="button" class="rq-qty__btn" aria-label="More" @click="form.quantity = Number(form.quantity || 1) + 1">+</button>
                                    </div>
                                </div>
                            </div>

                            <div class="rq-field">
                                <label class="rq-label" for="rq-desc">Tell us about it <span class="rq-required">(required)</span></label>
                                <textarea
                                    id="rq-desc"
                                    v-model="form.description"
                                    class="rq-input rq-textarea"
                                    rows="5"
                                    placeholder="Where will it go, what style, what colours, anything it must have. Example: “A 3-seater with a chaise on the left, beige fabric, wooden legs, to match our oak dining table.”"
                                    @input="clearError('description')"
                                ></textarea>
                                <div class="rq-help-row">
                                    <FieldError :message="fieldError('description')" />
                                    <span class="rq-counter" :class="{ 'is-low': form.description.length > 0 && form.description.length < 20 }">{{ form.description.length }} characters</span>
                                </div>
                            </div>

                            <!-- Photos -->
                            <div class="rq-field">
                                <label class="rq-label">Photos or sketches <span class="rq-optional">optional, up to {{ MAX_FILES }}</span></label>
                                <div
                                    class="rq-drop"
                                    :class="{ 'is-over': dragOver }"
                                    @dragover.prevent="dragOver = true"
                                    @dragleave.prevent="dragOver = false"
                                    @drop.prevent="onDrop"
                                >
                                    <input ref="fileInput" type="file" accept="image/*" multiple class="rq-drop__input" @change="onFilesChange">
                                    <div class="rq-drop__text">
                                        <strong>Add photos</strong> — a reference from Pinterest, a photo of the room, or a quick sketch
                                        <span class="rq-drop__hint">JPG, PNG or WEBP · up to 6 MB each</span>
                                    </div>
                                </div>
                                <div v-if="files.length" class="rq-thumbs">
                                    <div v-for="entry in files" :key="entry.key" class="rq-thumb">
                                        <img :src="entry.url" :alt="entry.file.name" />
                                        <button type="button" class="rq-thumb__remove" :aria-label="`Remove ${entry.file.name}`" @click="removeFile(entry.key)">×</button>
                                    </div>
                                </div>
                                <FieldError :message="fileError || fieldError('reference_images') || firstIndexedError('reference_images')" />
                            </div>

                            <!-- AI assistant -->
                            <div v-if="aiEnabled" class="rq-ai" :class="{ 'is-busy': aiBusy }">
                                <div class="rq-ai__head">
                                    <span class="rq-ai__icon" aria-hidden="true">✦</span>
                                    <div>
                                        <div class="rq-ai__title">Not sure how to describe it? Let the assistant draft it.</div>
                                        <div class="rq-ai__text">Add a photo or a sketch above, or a sentence in the box, and it fills in the name, type, description and preferences for you to review.</div>
                                    </div>
                                </div>
                                <div class="rq-ai__actions">
                                    <button type="button" class="btn btn-outline btn-sm" :disabled="aiBusy || (!form.description.trim() && !files.length)" data-text="Draft for me" @click="draftWithAi">
                                        <span>{{ aiBusy ? 'Reading your request…' : 'Draft for me' }}</span>
                                    </button>
                                    <button v-if="aiSnapshot" type="button" class="rq-note__link" @click="undoAiDraft">Undo</button>
                                    <span v-if="aiError" class="rq-error" role="alert">{{ aiError }}</span>
                                    <span v-else-if="aiApplied" class="rq-ai__done">Filled in {{ aiApplied }} — please check it and adjust anything that is not right.</span>
                                </div>
                                <div v-if="aiQuestions.length" class="rq-ai__questions">
                                    <div class="rq-ai__qtitle">It would help to know:</div>
                                    <button v-for="question in aiQuestions" :key="question" type="button" class="rq-ai__q" @click="answerQuestion(question)">
                                        {{ question }} <span>answer ↓</span>
                                    </button>
                                </div>
                            </div>

                            <!-- Dimensions -->
                            <details class="rq-details" :open="hasDimensions">
                                <summary class="rq-details__summary">Do you have measurements? <span class="rq-optional">optional</span></summary>
                                <div class="rq-dims">
                                    <div class="rq-dims__sketch" aria-hidden="true">
                                        <svg viewBox="0 0 120 90" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <path d="M20 60 L20 30 L70 30 L100 15 L100 45 L70 60 Z"/><path d="M20 60 L70 60 L100 45"/><path d="M70 30 L70 60"/>
                                            <path d="M20 72 L70 72" stroke-dasharray="3 3"/><text x="38" y="84" font-size="9" fill="currentColor" stroke="none">W</text>
                                            <path d="M8 30 L8 60" stroke-dasharray="3 3"/><text x="2" y="48" font-size="9" fill="currentColor" stroke="none">H</text>
                                            <path d="M78 68 L108 53" stroke-dasharray="3 3"/><text x="98" y="70" font-size="9" fill="currentColor" stroke="none">D</text>
                                        </svg>
                                    </div>
                                    <div class="rq-dims__fields">
                                        <div class="rq-field">
                                            <label class="rq-label" for="rq-w">Width</label>
                                            <input id="rq-w" v-model="form.desired_width" class="rq-input" type="number" min="0" step="0.5" inputmode="decimal" placeholder="220" @input="clearError('desired_width')">
                                            <FieldError :message="fieldError('desired_width')" />
                                        </div>
                                        <div class="rq-field">
                                            <label class="rq-label" for="rq-h">Height</label>
                                            <input id="rq-h" v-model="form.desired_height" class="rq-input" type="number" min="0" step="0.5" inputmode="decimal" placeholder="85" @input="clearError('desired_height')">
                                            <FieldError :message="fieldError('desired_height')" />
                                        </div>
                                        <div class="rq-field">
                                            <label class="rq-label" for="rq-d">Depth</label>
                                            <input id="rq-d" v-model="form.desired_depth" class="rq-input" type="number" min="0" step="0.5" inputmode="decimal" placeholder="95" @input="clearError('desired_depth')">
                                            <FieldError :message="fieldError('desired_depth')" />
                                        </div>
                                        <div class="rq-field">
                                            <label class="rq-label" for="rq-unit">Unit</label>
                                            <select id="rq-unit" v-model="form.dimension_unit" class="rq-input rq-select">
                                                <option value="cm">cm</option>
                                                <option value="mm">mm</option>
                                                <option value="in">inches</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </details>
                        </section>

                        <!-- ===== Step 2: Preferences ===== -->
                        <section v-show="step === 2" class="rq-step">
                            <h3 class="rq-step__title">Any preferences?</h3>
                            <p class="rq-step__intro">Everything here is optional. Skip what you are not sure about — that is what the quotation call is for.</p>

                            <div v-if="modifiersLoading" class="rq-skeleton">
                                <div v-for="n in 2" :key="n" class="rq-skeleton__group">
                                    <div class="rq-skeleton__line"></div>
                                    <div class="rq-skeleton__cards"><div v-for="m in 3" :key="m"></div></div>
                                </div>
                            </div>

                            <OptionGroups
                                v-else-if="modifierGroups.length"
                                :groups="modifierGroups"
                                :is-selected="isOptionSelected"
                                :summary="selectedModifierSummary"
                                :show-prices="false"
                                :show-required="false"
                                show-descriptions
                                clearable
                                @toggle="toggleModifierSelection"
                                @clear="clearModifier"
                            />
                            <p v-else class="rq-muted">No material or finish options to pick for this type of piece — just tell us in the description.</p>
                            <FieldError :message="fieldError('selected_modifiers')" />

                            <div class="rq-divider"></div>

                            <div class="rq-grid rq-grid--3">
                                <div class="rq-field">
                                    <label class="rq-label" for="rq-bmin">Budget from <span class="rq-optional">{{ currency }}</span></label>
                                    <input id="rq-bmin" v-model="form.budget_min" class="rq-input" type="number" min="0" step="50" inputmode="numeric" placeholder="500" @input="clearError('budget_min'); clearError('budget_max')">
                                    <FieldError :message="fieldError('budget_min')" />
                                </div>
                                <div class="rq-field">
                                    <label class="rq-label" for="rq-bmax">Budget up to <span class="rq-optional">{{ currency }}</span></label>
                                    <input id="rq-bmax" v-model="form.budget_max" class="rq-input" type="number" min="0" step="50" inputmode="numeric" placeholder="1200" @input="clearError('budget_max')">
                                    <FieldError :message="fieldError('budget_max')" />
                                </div>
                                <div class="rq-field">
                                    <label class="rq-label" for="rq-date">Needed by <span class="rq-optional">optional</span></label>
                                    <input id="rq-date" v-model="form.target_date" class="rq-input" type="date" :min="today" @input="clearError('target_date')">
                                    <FieldError :message="fieldError('target_date')" />
                                </div>
                            </div>
                            <p class="rq-help">A budget range helps us suggest the right materials — it is not binding.</p>
                        </section>

                        <!-- ===== Step 3: Contact & review ===== -->
                        <section v-show="step === 3" class="rq-step">
                            <h3 class="rq-step__title">Where should we send the invoice?</h3>

                            <div v-if="session.isAuthenticated.value && !editingContact" class="rq-contact-card">
                                <div>
                                    <div class="rq-contact-card__name">{{ form.contact_name }}</div>
                                    <div class="rq-contact-card__line">{{ form.contact_email }}<span v-if="form.contact_phone"> · {{ form.contact_phone }}</span></div>
                                    <div v-if="!form.contact_phone" class="rq-contact-card__warn">Add a phone number so we can call you about the details.</div>
                                </div>
                                <button type="button" class="rq-note__link" @click="editingContact = true">Change</button>
                            </div>

                            <div v-else class="rq-grid">
                                <div class="rq-field">
                                    <label class="rq-label" for="rq-name">Your name <span class="rq-required">(required)</span></label>
                                    <input id="rq-name" v-model="form.contact_name" class="rq-input" type="text" autocomplete="name" placeholder="Maya Hamadeh" @input="clearError('contact_name')">
                                    <FieldError :message="fieldError('contact_name')" />
                                </div>
                                <div class="rq-field">
                                    <label class="rq-label" for="rq-phone">Phone <span class="rq-required">(required)</span></label>
                                    <input id="rq-phone" v-model="form.contact_phone" class="rq-input" type="tel" autocomplete="tel" inputmode="tel" placeholder="03 123 456" @input="clearError('contact_phone')">
                                    <FieldError :message="fieldError('contact_phone')" />
                                    <label class="rq-check">
                                        <input v-model="form.whatsapp_same" type="checkbox"> This number is on WhatsApp
                                    </label>
                                </div>
                                <div class="rq-field rq-field--full">
                                    <label class="rq-label" for="rq-email">Email <span class="rq-required">(required)</span></label>
                                    <input id="rq-email" v-model="form.contact_email" class="rq-input" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" @input="clearError('contact_email')">
                                    <FieldError :message="fieldError('contact_email')" />
                                    <span class="rq-help">The invoice comes here, with a link to accept it online.</span>
                                </div>
                            </div>

                            <!-- Honeypot: hidden from people, filled by bots -->
                            <div class="rq-hp" aria-hidden="true">
                                <label for="rq-fax">Fax number</label>
                                <input id="rq-fax" v-model="form.fax_number" type="text" tabindex="-1" autocomplete="off">
                            </div>

                            <div class="rq-divider"></div>

                            <h4 class="rq-review__title">Your request</h4>
                            <dl class="rq-review">
                                <div><dt>Piece</dt><dd>{{ form.title || '—' }}<span v-if="Number(form.quantity) > 1"> × {{ form.quantity }}</span><span v-if="itemTypeLabel" class="rq-review__muted"> · {{ itemTypeLabel }}</span></dd></div>
                                <div><dt>Details</dt><dd class="rq-review__desc">{{ form.description || '—' }}</dd></div>
                                <div v-if="hasDimensions"><dt>Size</dt><dd>{{ dimensionSummary }}</dd></div>
                                <div v-if="preferenceSummary.length"><dt>Preferences</dt><dd><span v-for="line in preferenceSummary" :key="line" class="rq-review__chip">{{ line }}</span></dd></div>
                                <div v-if="form.budget_min || form.budget_max"><dt>Budget</dt><dd>{{ budgetSummary }}</dd></div>
                                <div v-if="form.target_date"><dt>Needed by</dt><dd>{{ form.target_date }}</dd></div>
                                <div v-if="files.length"><dt>Photos</dt><dd>{{ files.length }} attached</dd></div>
                            </dl>
                            <button type="button" class="rq-note__link" @click="goTo(1)">Edit details</button>
                        </section>

                        <!-- ===== Errors + navigation ===== -->
                        <div v-if="generalError" class="rq-alert" role="alert">
                            {{ generalError }}
                        </div>
                        <div v-else-if="hasErrors && step === 3" class="rq-alert" role="alert">
                            Please check the highlighted fields{{ firstErrorStep !== 3 ? ` on step ${firstErrorStep}` : '' }}.
                            <button v-if="firstErrorStep !== 3" type="button" class="rq-alert__link" @click="goTo(firstErrorStep)">Take me there</button>
                        </div>

                        <div class="rq-nav">
                            <button v-if="step > 1" type="button" class="btn btn-outline" data-text="Back" @click="goTo(step - 1)"><span>Back</span></button>
                            <a v-else :href="whatsappHref" target="_blank" rel="noopener noreferrer" class="rq-whatsapp">
                                <i class="fa-brands fa-whatsapp"></i> Prefer to chat? WhatsApp us
                            </a>
                            <div class="rq-nav__spacer"></div>
                            <span v-if="!canProceed" class="rq-nav__hint">{{ step === 3 ? 'Fill in your name, phone and email to send.' : 'Fill in the required fields to continue.' }}</span>
                            <button v-if="step < 3" type="button" class="btn btn-solid" data-text="Continue" :disabled="!canProceed" @click="next"><span>Continue</span></button>
                            <button v-else type="button" class="btn btn-solid" :data-text="submitting ? 'Sending…' : 'Send request'" :disabled="submitting || !canProceed" @click="submitRequest">
                                <span>{{ submitting ? 'Sending…' : 'Send request' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne />
        <ScrollToTop />
    </div>
</template>

<script setup>
import { computed, defineComponent, h, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Aos from 'aos';
import NavbarOne from '@/components/navbar/navbar-one.vue';
import FooterOne from '@/components/footer/footer-one.vue';
import ScrollToTop from '@/components/scroll-to-top.vue';
import OptionGroups from '@/components/product/option-groups.vue';
import breadcumbBg from '@/assets/img/shortcode/breadcumb.jpg';
import { storefrontRequest } from '@/lib/storefront-api';
import { useClientSession } from '@/composables/use-client-session';
import { whatsappLink } from '@/data/company';

const FieldError = defineComponent({
    props: { message: { type: String, default: '' } },
    setup: (props) => () => props.message ? h('p', { class: 'rq-error', role: 'alert' }, props.message) : null,
});

const STEP_LABELS = ['What you need', 'Preferences', 'Contact & send'];
const MAX_FILES = 6;
const MAX_FILE_BYTES = 6 * 1024 * 1024;
const DRAFT_KEY = 'quote-request-draft';
const QUOTE_PREFILL_STORAGE_KEY = 'quote-product-prefill';

const ITEM_TYPES = [
    { value: 'seating', label: 'Sofas & seating', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3"/><path d="M3 13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3H3z"/><path d="M5 16v2M19 16v2"/></svg>' },
    { value: 'tables', label: 'Tables & desks', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 8h18"/><path d="M5 8v10M19 8v10"/><path d="M5 13h14"/></svg>' },
    { value: 'bedroom', label: 'Beds & bedroom', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7"/><path d="M3 14h18"/><path d="M6 9V6h12v3"/></svg>' },
    { value: 'storage', label: 'Storage & units', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="4" y="3" width="16" height="18" rx="1"/><path d="M4 9h16M4 15h16"/><path d="M12 5v2M12 11v2M12 17v2"/></svg>' },
    { value: 'outdoor', label: 'Outdoor', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 3v18"/><path d="M4 11a8 8 0 0 1 16 0z"/><path d="M8 21h8"/></svg>' },
    { value: 'other', label: 'Something else', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>' },
];

const route = useRoute();
const router = useRouter();
const session = useClientSession();

const step = ref(1);
const furthestStep = ref(1);
const submitting = ref(false);
const success = ref(null);
const errors = ref({});
const fileError = ref('');
const fileInput = ref(null);
const dragOver = ref(false);
const files = ref([]);
const modifierLibrary = ref([]);
const modifiersLoading = ref(false);
const selectedModifiers = reactive({});
const linkedProduct = ref(null);
const restoredDraft = ref(false);
const editingContact = ref(false);
const currency = 'USD';
const today = new Date().toISOString().slice(0, 10);
const whatsappHref = whatsappLink('Hello Sweet Home Gallery, I would like a quotation for a custom piece.');

const blankForm = () => ({
    product_id: '',
    item_type: '',
    title: '',
    quantity: 1,
    description: '',
    desired_width: '',
    desired_height: '',
    desired_depth: '',
    dimension_unit: 'cm',
    budget_min: '',
    budget_max: '',
    target_date: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    whatsapp_same: true,
    fax_number: '',
});

const form = reactive(blankForm());

// ---------- errors ----------
const STEP_FIELDS = {
    1: ['item_type', 'title', 'quantity', 'description', 'desired_width', 'desired_height', 'desired_depth', 'dimension_unit', 'reference_images'],
    2: ['selected_modifiers', 'budget_min', 'budget_max', 'target_date'],
    3: ['contact_name', 'contact_phone', 'contact_email', 'fax_number'],
};

const fieldError = (key) => errors.value[key]?.[0] || '';
const firstIndexedError = (prefix) => {
    const key = Object.keys(errors.value).find((entry) => entry.startsWith(`${prefix}.`));
    return key ? errors.value[key][0] : '';
};
const clearError = (key) => {
    if (errors.value[key]) {
        const next = { ...errors.value };
        delete next[key];
        errors.value = next;
    }
};
const generalError = computed(() => errors.value.general?.[0] || '');
const hasErrors = computed(() => Object.keys(errors.value).some((key) => key !== 'general'));
const stepHasError = (target) => Object.keys(errors.value).some((key) => STEP_FIELDS[target].some((field) => key === field || key.startsWith(`${field}.`)));
const firstErrorStep = computed(() => [1, 2, 3].find((target) => stepHasError(target)) || 3);

// ---------- steps ----------
const scrollToForm = () => {
    const card = document.querySelector('.rq-card');
    if (card) window.scrollTo({ top: card.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
};

const goTo = (target) => {
    step.value = target;
    furthestStep.value = Math.max(furthestStep.value, target);
    nextTick(scrollToForm);
};

const validateStep1 = () => {
    const next = {};
    if (!form.title.trim()) next.title = ['Give the piece a short name, e.g. "L-shaped sofa".'];
    if (!form.description.trim()) next.description = ['Tell us a little about what you need — even two lines help.'];
    if (Object.keys(next).length) {
        errors.value = { ...errors.value, ...next };
        return false;
    }
    return true;
};

const step1Complete = computed(() => form.title.trim().length > 0 && form.description.trim().length > 0);
const step3Complete = computed(() =>
    form.contact_name.trim().length > 0
    && form.contact_phone.trim().length > 0
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim()));
// The primary button only unlocks once the step's required fields are filled.
const canProceed = computed(() => (step.value === 1 ? step1Complete.value : step.value === 3 ? step3Complete.value : true));

const next = () => {
    if (step.value === 1 && !validateStep1()) return;
    goTo(step.value + 1);
};

// ---------- item type & modifiers ----------
const itemTypeLabel = computed(() => ITEM_TYPES.find((type) => type.value === form.item_type)?.label || '');

async function loadModifiers() {
    modifiersLoading.value = true;
    try {
        const response = await storefrontRequest('/modifiers', { params: form.item_type ? { item_type: form.item_type } : {} });
        modifierLibrary.value = (response.data || []).map((modifier) => ({ ...modifier, options: modifier.options || [] }));
        // Drop selections that no longer apply; never pre-select anything.
        Object.keys(selectedModifiers).forEach((key) => {
            if (!modifierLibrary.value.some((modifier) => String(modifier.id) === String(key))) delete selectedModifiers[key];
        });
    } catch (e) {
        modifierLibrary.value = [];
    } finally {
        modifiersLoading.value = false;
    }
}

const selectItemType = (value) => {
    form.item_type = form.item_type === value ? '' : value;
    clearError('item_type');
    loadModifiers();
};

const modifierGroups = computed(() => modifierLibrary.value);

function isOptionSelected(modifierId, optionId) {
    const current = selectedModifiers[modifierId];
    return Array.isArray(current) ? current.includes(optionId) : current === optionId;
}

function toggleModifierSelection(modifier, option) {
    clearError('selected_modifiers');
    if (modifier.selection_type === 'multiple') {
        const current = Array.isArray(selectedModifiers[modifier.id]) ? [...selectedModifiers[modifier.id]] : [];
        const index = current.indexOf(option.id);
        index >= 0 ? current.splice(index, 1) : current.push(option.id);
        selectedModifiers[modifier.id] = current;
        return;
    }
    // Clicking the selected option again clears it — nothing is ever forced.
    selectedModifiers[modifier.id] = selectedModifiers[modifier.id] === option.id ? null : option.id;
}

function clearModifier(modifier) {
    selectedModifiers[modifier.id] = modifier.selection_type === 'multiple' ? [] : null;
}

function selectedOptionsFor(modifier) {
    const current = selectedModifiers[modifier.id];
    const ids = Array.isArray(current) ? current : current ? [current] : [];
    return modifier.options.filter((option) => ids.includes(option.id));
}

function selectedModifierSummary(modifier) {
    return selectedOptionsFor(modifier).map((option) => option.label).join(', ');
}

function buildSelectedModifiersPayload() {
    return modifierGroups.value.reduce((payload, modifier) => {
        const options = selectedOptionsFor(modifier).map((option) => ({
            option_id: option.id,
            label: option.label,
            level: option.level || null,
        }));
        if (options.length) {
            payload.push({ modifier_id: modifier.id, modifier_name: modifier.name, selection_type: modifier.selection_type, options });
        }
        return payload;
    }, []);
}

// Legacy free-text fields the admin still shows; derived from the matching groups.
function legacyPreference(needles) {
    const group = modifierGroups.value.find((modifier) => needles.some((needle) => `${modifier.name} ${modifier.slug || ''}`.toLowerCase().includes(needle)));
    return group ? selectedModifierSummary(group) : '';
}

// ---------- files ----------
const addFiles = (incoming) => {
    fileError.value = '';
    const accepted = [];
    for (const file of incoming) {
        if (!file.type.startsWith('image/')) { fileError.value = `${file.name} is not an image.`; continue; }
        if (file.size > MAX_FILE_BYTES) { fileError.value = `${file.name} is over 6 MB — try a smaller photo.`; continue; }
        if (files.value.length + accepted.length >= MAX_FILES) { fileError.value = `You can attach up to ${MAX_FILES} photos.`; break; }
        accepted.push({ key: `${file.name}-${file.size}-${file.lastModified}`, file, url: URL.createObjectURL(file) });
    }
    const existing = new Set(files.value.map((entry) => entry.key));
    files.value = [...files.value, ...accepted.filter((entry) => !existing.has(entry.key))];
    clearError('reference_images');
};

const onFilesChange = (event) => {
    addFiles(Array.from(event.target.files || []));
    event.target.value = '';
};

const onDrop = (event) => {
    dragOver.value = false;
    addFiles(Array.from(event.dataTransfer?.files || []));
};

const removeFile = (key) => {
    const entry = files.value.find((item) => item.key === key);
    if (entry) URL.revokeObjectURL(entry.url);
    files.value = files.value.filter((item) => item.key !== key);
};

// ---------- summaries ----------
const hasDimensions = computed(() => [form.desired_width, form.desired_height, form.desired_depth].some((value) => value !== '' && value !== null));
const dimensionSummary = computed(() => {
    const parts = [];
    if (form.desired_width) parts.push(`${form.desired_width} W`);
    if (form.desired_height) parts.push(`${form.desired_height} H`);
    if (form.desired_depth) parts.push(`${form.desired_depth} D`);
    return `${parts.join(' × ')} ${form.dimension_unit}`;
});
const preferenceSummary = computed(() => modifierGroups.value
    .map((modifier) => ({ name: modifier.name, value: selectedModifierSummary(modifier) }))
    .filter((entry) => entry.value)
    .map((entry) => `${entry.name}: ${entry.value}`));
const budgetSummary = computed(() => {
    const min = form.budget_min ? `${currency} ${Number(form.budget_min).toLocaleString()}` : '';
    const max = form.budget_max ? `${currency} ${Number(form.budget_max).toLocaleString()}` : '';
    if (min && max) return `${min} – ${max}`;
    return min ? `from ${min}` : `up to ${max}`;
});

// ---------- draft persistence ----------
const DRAFT_FIELDS = ['item_type', 'title', 'quantity', 'description', 'desired_width', 'desired_height', 'desired_depth', 'dimension_unit', 'budget_min', 'budget_max', 'target_date', 'contact_name', 'contact_phone', 'contact_email', 'whatsapp_same'];

let draftTimer = null;
const writeDraft = () => {
    if (success.value) return;
    const data = Object.fromEntries(DRAFT_FIELDS.map((key) => [key, form[key]]));
    const hasContent = data.title || data.description || data.item_type;
    if (!hasContent) { localStorage.removeItem(DRAFT_KEY); return; }
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: data, selected: { ...selectedModifiers }, step: step.value }));
};
const saveDraft = () => {
    clearTimeout(draftTimer);
    draftTimer = setTimeout(writeDraft, 400);
};
// A refresh or tab close inside the debounce window must not lose the last keystrokes.
const flushDraft = () => { clearTimeout(draftTimer); writeDraft(); };

const restoreDraft = () => {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return false;
        const draft = JSON.parse(raw);
        Object.assign(form, draft.form || {});
        Object.assign(selectedModifiers, draft.selected || {});
        restoredDraft.value = true;
        return true;
    } catch (e) {
        localStorage.removeItem(DRAFT_KEY);
        return false;
    }
};

const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    const contact = { contact_name: form.contact_name, contact_phone: form.contact_phone, contact_email: form.contact_email };
    Object.assign(form, blankForm(), session.isAuthenticated.value ? contact : {});
    Object.keys(selectedModifiers).forEach((key) => delete selectedModifiers[key]);
    files.value.forEach((entry) => URL.revokeObjectURL(entry.url));
    files.value = [];
    errors.value = {};
    restoredDraft.value = false;
    linkedProduct.value = null;
    goTo(1);
    loadModifiers();
};

watch(form, saveDraft, { deep: true });
watch(selectedModifiers, saveDraft, { deep: true });

// ---------- profile & product prefill ----------
function fillFromProfile(user) {
    if (!user) return;
    if (!form.contact_name) form.contact_name = user.name || '';
    if (!form.contact_phone) form.contact_phone = user.phone || '';
    if (!form.contact_email) form.contact_email = user.email || '';
}

watch(() => session.state.user, fillFromProfile, { immediate: true });

function applyProductPrefill() {
    if (route.query.quote_prefill !== 'product-page') return false;
    const raw = sessionStorage.getItem(QUOTE_PREFILL_STORAGE_KEY);
    if (!raw) return false;

    try {
        const prefill = JSON.parse(raw);
        linkedProduct.value = prefill;
        form.product_id = prefill.product_id || '';
        form.title = prefill.title || prefill.name || '';
        form.quantity = Number(prefill.quantity || 1);
        form.item_type = prefill.item_type || '';
        form.desired_width = prefill.desired_width ?? '';
        form.desired_height = prefill.desired_height ?? '';
        form.desired_depth = prefill.desired_depth ?? '';
        form.dimension_unit = prefill.dimension_unit || 'cm';
        form.description = prefill.selected_summary
            ? `As shown on the site with: ${prefill.selected_summary}.\n\n`
            : '';
        Object.keys(selectedModifiers).forEach((key) => delete selectedModifiers[key]);
        (prefill.selected_modifiers || []).forEach((modifier) => {
            const ids = (modifier.options || []).map((option) => option.option_id).filter(Boolean);
            if (modifier.modifier_id) selectedModifiers[modifier.modifier_id] = modifier.selection_type === 'multiple' ? ids : (ids[0] || null);
        });
        return true;
    } catch (e) {
        sessionStorage.removeItem(QUOTE_PREFILL_STORAGE_KEY);
        return false;
    }
}

const unlinkProduct = () => {
    linkedProduct.value = null;
    form.product_id = '';
    sessionStorage.removeItem(QUOTE_PREFILL_STORAGE_KEY);
    if (route.query.quote_prefill) router.replace({ path: route.path });
};

// ---------- AI assistant ----------
const aiEnabled = ref(false);
const aiBusy = ref(false);
const aiError = ref('');
const aiApplied = ref('');
const aiQuestions = ref([]);
const aiSnapshot = ref(null);

async function loadAiStatus() {
    try {
        const status = await storefrontRequest('/ai/status');
        aiEnabled.value = !!status.features?.draft_request;
    } catch (e) {
        aiEnabled.value = false;
    }
}

async function draftWithAi() {
    aiBusy.value = true;
    aiError.value = '';
    aiApplied.value = '';
    aiQuestions.value = [];

    const payload = new FormData();
    payload.append('text', form.description.trim());
    if (form.item_type) payload.append('item_type', form.item_type);
    files.value.slice(0, 3).forEach((entry) => payload.append('images[]', entry.file));

    try {
        const response = await storefrontRequest('/ai/draft-request', { method: 'POST', data: payload });
        await applyAiDraft(response.data || {});
    } catch (error) {
        aiError.value = error.status === 429
            ? 'The assistant is taking a breather — try again in a minute, or just keep typing.'
            : (error.message || 'The assistant could not help with this one. You can still fill the form yourself.');
    } finally {
        aiBusy.value = false;
    }
}

async function applyAiDraft(result) {
    const draft = result.draft || {};
    aiSnapshot.value = {
        item_type: form.item_type, title: form.title, description: form.description, quantity: form.quantity,
        desired_width: form.desired_width, desired_height: form.desired_height, desired_depth: form.desired_depth,
        dimension_unit: form.dimension_unit, selected: { ...selectedModifiers },
    };
    const filled = [];

    if (draft.item_type && draft.item_type !== form.item_type) {
        form.item_type = draft.item_type;
        filled.push('the type');
        await loadModifiers();
    }
    if (draft.title) { form.title = draft.title; filled.push('a name'); }
    if (draft.description) { form.description = draft.description; filled.push('the description'); }
    if (draft.quantity > 1) form.quantity = draft.quantity;

    const dims = draft.dimensions || {};
    if (dims.width || dims.height || dims.depth) {
        form.desired_width = dims.width ?? '';
        form.desired_height = dims.height ?? '';
        form.desired_depth = dims.depth ?? '';
        form.dimension_unit = dims.unit || 'cm';
        filled.push('measurements');
    }

    let prefs = 0;
    (draft.preferences || []).forEach((pref) => {
        if (!modifierLibrary.value.some((modifier) => modifier.id === pref.modifier_id)) return;
        selectedModifiers[pref.modifier_id] = pref.selection_type === 'multiple' ? pref.option_ids : (pref.option_ids[0] || null);
        prefs++;
    });
    if (prefs) filled.push(`${prefs} preference${prefs > 1 ? 's' : ''}`);

    aiQuestions.value = result.questions || [];
    aiApplied.value = filled.length ? filled.join(', ') : 'nothing new';
    clearError('title'); clearError('description');
}

function undoAiDraft() {
    const snap = aiSnapshot.value;
    if (!snap) return;
    Object.assign(form, {
        item_type: snap.item_type, title: snap.title, description: snap.description, quantity: snap.quantity,
        desired_width: snap.desired_width, desired_height: snap.desired_height, desired_depth: snap.desired_depth, dimension_unit: snap.dimension_unit,
    });
    Object.keys(selectedModifiers).forEach((key) => delete selectedModifiers[key]);
    Object.assign(selectedModifiers, snap.selected);
    aiSnapshot.value = null;
    aiApplied.value = '';
    aiQuestions.value = [];
    loadModifiers();
}

function answerQuestion(question) {
    const prefix = form.description.trim() ? '\n\n' : '';
    form.description = `${form.description.trimEnd()}${prefix}${question} `;
    aiQuestions.value = aiQuestions.value.filter((entry) => entry !== question);
    nextTick(() => {
        const box = document.getElementById('rq-desc');
        if (box) { box.focus(); box.setSelectionRange(box.value.length, box.value.length); }
    });
}

// ---------- submit ----------
async function submitRequest() {
    errors.value = {};
    if (!validateStep1()) { goTo(1); return; }

    submitting.value = true;
    const payload = new FormData();
    const fields = {
        product_id: form.product_id,
        item_type: form.item_type,
        title: form.title.trim(),
        quantity: Math.max(1, Number(form.quantity || 1)),
        description: form.description.trim(),
        desired_width: form.desired_width,
        desired_height: form.desired_height,
        desired_depth: form.desired_depth,
        dimension_unit: form.dimension_unit,
        budget_min: form.budget_min,
        budget_max: form.budget_max,
        target_date: form.target_date,
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim(),
        contact_email: form.contact_email.trim(),
        contact_whatsapp: form.whatsapp_same ? form.contact_phone.trim() : '',
        preferred_material: legacyPreference(['wood', 'material', 'finish']),
        preferred_fabric: legacyPreference(['fabric', 'leather', 'upholstery']),
        fax_number: form.fax_number,
    };
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) payload.append(key, value);
    });
    files.value.forEach((entry) => payload.append('reference_images[]', entry.file));
    const modifiers = buildSelectedModifiersPayload();
    if (modifiers.length) payload.append('selected_modifiers', JSON.stringify(modifiers));

    try {
        const response = await storefrontRequest('/custom-requests/website', { method: 'POST', data: payload });
        success.value = {
            reference: response.data?.reference || '',
            customer_mail_sent: !!response.data?.customer_mail_sent,
            has_account: !!response.data?.has_account,
            email: form.contact_email,
        };
        localStorage.removeItem(DRAFT_KEY);
        sessionStorage.removeItem(QUOTE_PREFILL_STORAGE_KEY);
        nextTick(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
    } catch (error) {
        const serverErrors = error.errors && Object.keys(error.errors).length ? error.errors : null;
        if (error.status === 429) {
            errors.value = { general: ['Too many requests in a short time. Please wait a minute and press Send again — nothing you typed is lost.'] };
        } else if (serverErrors) {
            errors.value = serverErrors;
            const target = firstErrorStep.value;
            if (target !== step.value) goTo(target);
            else nextTick(scrollToForm);
        } else {
            errors.value = { general: [error.message || 'We could not send your request. Check your connection and try again — your text is saved.'] };
        }
    } finally {
        submitting.value = false;
    }
}

onBeforeUnmount(() => {
    flushDraft();
    window.removeEventListener('beforeunload', flushDraft);
});

const startOver = () => {
    success.value = null;
    clearDraft();
};

onMounted(async () => {
    Aos.init();
    window.scrollTo(0, 0);
    window.addEventListener('beforeunload', flushDraft);
    session.initializeSession().catch(() => null);

    const prefilled = applyProductPrefill();
    if (!prefilled) restoreDraft();
    loadAiStatus();
    await loadModifiers();
});
</script>

<style scoped>
.rq-card {
    background: #fff;
    border: 1px solid #e8e9ea;
    border-radius: 16px;
    padding: 28px;
}

/* Progress */
.rq-steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin: 0 0 26px;
    padding: 0;
    list-style: none;
}
.rq-steps__btn {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 0 10px;
    border-bottom: 3px solid #e8e9ea;
    color: #8a9197;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    transition: color 0.2s ease, border-color 0.2s ease;
}
.rq-steps__btn:disabled { cursor: default; }
.rq-steps__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    border-radius: 999px;
    border: 1.5px solid currentColor;
    font-size: 12px;
}
.rq-steps__item.is-active .rq-steps__btn { color: #172430; border-color: #bb976d; }
.rq-steps__item.is-active .rq-steps__num { background: #bb976d; border-color: #bb976d; color: #fff; }
.rq-steps__item.is-done .rq-steps__btn { color: #bb976d; border-color: #bb976d; }
.rq-steps__item.has-error .rq-steps__btn { color: #b42318; border-color: #b42318; }
@media (max-width: 560px) { .rq-steps__label { display: none; } .rq-steps__item.is-active .rq-steps__label { display: inline; } }

/* Linked product */
.rq-product {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    margin-bottom: 20px;
    border: 1px solid rgba(187, 151, 109, 0.35);
    background: rgba(187, 151, 109, 0.07);
    border-radius: 12px;
}
.rq-product__img { width: 56px; height: 56px; border-radius: 8px; object-fit: cover; flex: 0 0 56px; }
.rq-product__body { min-width: 0; flex: 1; }
.rq-product__eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #bb976d; font-weight: 600; }
.rq-product__name { font-weight: 600; color: #172430; }
.rq-product__sku { font-weight: 400; color: #8a9197; font-size: 13px; margin-left: 4px; }
.rq-product__opts { font-size: 13px; color: #3c474e; margin-top: 2px; }
.rq-product__remove { font-size: 22px; line-height: 1; color: #8a9197; padding: 4px 8px; }
.rq-product__remove:hover { color: #172430; }

.rq-note { margin-bottom: 18px; font-size: 13px; color: #3c474e; }
.rq-note__link { font-size: 13px; color: #bb976d; text-decoration: underline; text-underline-offset: 2px; }

/* Steps */
.rq-step__title { font-size: 22px; font-weight: 600; margin: 0 0 4px; }
.rq-step__intro { color: #8a9197; font-size: 14px; margin: 0 0 22px; }

.rq-field { margin-bottom: 18px; }
.rq-field--full { grid-column: 1 / -1; }
.rq-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 16px; }
.rq-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.rq-field--grow { grid-column: span 1; }
.rq-field--qty { max-width: 170px; }
@media (max-width: 640px) { .rq-grid, .rq-grid--3 { grid-template-columns: 1fr; } .rq-field--qty { max-width: none; } }

.rq-label { display: block; font-size: 14px; font-weight: 600; color: #172430; margin-bottom: 7px; }
.rq-required {
    margin-left: 6px;
    color: #d0342c;
    font-size: 12px;
    font-weight: 500;
}
.rq-optional { font-weight: 400; color: #8a9197; font-size: 12px; margin-left: 4px; }
.rq-input {
    width: 100%;
    height: 48px;
    padding: 0 14px;
    border: 1px solid #dfe2e4;
    border-radius: 10px;
    background: #fff;
    color: #172430;
    font-size: 15px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.rq-input:focus { border-color: #bb976d; box-shadow: 0 0 0 3px rgba(187, 151, 109, 0.18); }
.rq-textarea { height: auto; padding: 12px 14px; resize: vertical; line-height: 1.5; }
.rq-select { appearance: auto; }
.rq-help { display: block; font-size: 12.5px; color: #8a9197; margin-top: 6px; }
.rq-help-row { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
.rq-counter { font-size: 12px; color: #8a9197; margin-top: 6px; margin-left: auto; }
.rq-counter.is-low { color: #b07600; }
.rq-muted { color: #8a9197; font-size: 14px; }
.rq-divider { height: 1px; background: #eceff1; margin: 8px 0 22px; }
.rq-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #3c474e; margin-top: 8px; }
.rq-check input { accent-color: #bb976d; width: 15px; height: 15px; }

/* Item type tiles */
.rq-types { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; }
@media (max-width: 720px) { .rq-types { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.rq-type {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 12px 6px 10px;
    border: 1px solid #e8e9ea;
    border-radius: 10px;
    font-size: 12.5px;
    font-weight: 500;
    color: #3c474e;
    text-align: center;
    line-height: 1.2;
    transition: border-color 0.18s ease, background-color 0.18s ease;
}
.rq-type:hover { border-color: #bb976d; }
.rq-type.is-selected { border-color: #bb976d; background: rgba(187, 151, 109, 0.08); box-shadow: inset 0 0 0 1px #bb976d; color: #172430; }
.rq-type__icon { width: 28px; height: 28px; color: #bb976d; }
.rq-type__icon :deep(svg) { width: 100%; height: 100%; }

/* Quantity */
.rq-qty { display: inline-flex; align-items: stretch; border: 1px solid #dfe2e4; border-radius: 10px; overflow: hidden; height: 48px; }
.rq-qty__btn { width: 44px; font-size: 20px; color: #3c474e; background: #f8f5f0; }
.rq-qty__btn:hover { background: #efe7dc; }
.rq-qty__input { width: 64px; text-align: center; border: 0; outline: none; font-size: 15px; color: #172430; -moz-appearance: textfield; }
.rq-qty__input::-webkit-inner-spin-button, .rq-qty__input::-webkit-outer-spin-button { -webkit-appearance: none; }

/* Files */
.rq-drop {
    position: relative;
    border: 1.5px dashed #d8d2c8;
    border-radius: 12px;
    padding: 18px;
    background: #faf8f5;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}
.rq-drop.is-over { border-color: #bb976d; background: rgba(187, 151, 109, 0.08); }
.rq-drop__input { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }
.rq-drop__text { font-size: 14px; color: #3c474e; text-align: center; line-height: 1.5; }
.rq-drop__hint { display: block; font-size: 12px; color: #8a9197; margin-top: 4px; }
.rq-thumbs { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.rq-thumb { position: relative; width: 76px; height: 76px; border-radius: 10px; overflow: hidden; border: 1px solid #e8e9ea; }
.rq-thumb img { width: 100%; height: 100%; object-fit: cover; }
.rq-thumb__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: rgba(23, 36, 48, 0.75);
    color: #fff;
    font-size: 15px;
    line-height: 1;
}

/* AI assistant */
.rq-ai {
    margin: 0 0 18px;
    padding: 14px 16px;
    border: 1px solid rgba(187, 151, 109, 0.4);
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(187, 151, 109, 0.10), rgba(187, 151, 109, 0.03));
}
.rq-ai.is-busy { opacity: 0.85; }
.rq-ai__head { display: flex; gap: 12px; align-items: flex-start; }
.rq-ai__icon { color: #bb976d; font-size: 18px; line-height: 1.2; }
.rq-ai__title { font-weight: 600; color: #172430; font-size: 14.5px; }
.rq-ai__text { font-size: 13px; color: #3c474e; margin-top: 2px; }
.rq-ai__actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.rq-ai__done { font-size: 13px; color: #1f7a3a; }
.rq-ai__questions { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.rq-ai__qtitle { font-size: 12.5px; color: #8a9197; width: 100%; }
.rq-ai__q {
    font-size: 13px;
    color: #172430;
    background: #fff;
    border: 1px solid #e8e9ea;
    border-radius: 999px;
    padding: 6px 12px;
    text-align: left;
}
.rq-ai__q span { color: #bb976d; font-size: 12px; margin-left: 4px; }
.rq-ai__q:hover { border-color: #bb976d; }

/* Dimensions */
.rq-details { border: 1px solid #e8e9ea; border-radius: 12px; padding: 0 16px; margin-bottom: 6px; }
.rq-details__summary { cursor: pointer; padding: 14px 0; font-size: 14px; font-weight: 600; color: #172430; list-style: none; display: flex; align-items: center; gap: 8px; }
.rq-details__summary::-webkit-details-marker { display: none; }
.rq-details__summary::before { content: ''; width: 7px; height: 7px; border-right: 1.5px solid #8a9197; border-bottom: 1.5px solid #8a9197; transform: rotate(-45deg); transition: transform 0.2s ease; }
.rq-details[open] .rq-details__summary::before { transform: rotate(45deg); }
.rq-dims { display: grid; grid-template-columns: 140px minmax(0, 1fr); gap: 16px; padding-bottom: 8px; align-items: start; }
.rq-dims__sketch { color: #8a9197; padding-top: 8px; }
.rq-dims__fields { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0 10px; }
@media (max-width: 640px) { .rq-dims { grid-template-columns: 1fr; } .rq-dims__sketch { display: none; } .rq-dims__fields { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

/* Skeleton */
.rq-skeleton__group { margin-bottom: 20px; }
.rq-skeleton__line { height: 14px; width: 140px; border-radius: 6px; background: #eceff1; margin-bottom: 10px; }
.rq-skeleton__cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.rq-skeleton__cards div { height: 54px; border-radius: 10px; background: #f1f3f4; animation: rq-pulse 1.2s ease-in-out infinite; }
@keyframes rq-pulse { 50% { opacity: 0.5; } }

/* Contact */
.rq-contact-card {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e8e9ea;
    border-radius: 12px;
    background: #faf8f5;
    margin-bottom: 18px;
}
.rq-contact-card__name { font-weight: 600; color: #172430; }
.rq-contact-card__line { font-size: 14px; color: #3c474e; margin-top: 2px; }
.rq-contact-card__warn { font-size: 13px; color: #b07600; margin-top: 6px; }
.rq-hp { position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden; }

/* Review */
.rq-review__title { font-size: 15px; font-weight: 600; margin: 0 0 10px; }
.rq-review { display: grid; gap: 8px; margin: 0 0 10px; }
.rq-review > div { display: grid; grid-template-columns: 110px minmax(0, 1fr); gap: 10px; font-size: 14px; }
.rq-review dt { color: #8a9197; }
.rq-review dd { margin: 0; color: #172430; }
.rq-review__desc { white-space: pre-line; }
.rq-review__muted { color: #8a9197; }
.rq-review__chip { display: inline-block; background: #f3f4f5; border-radius: 999px; padding: 3px 10px; font-size: 12.5px; margin: 0 6px 6px 0; }

/* Errors & nav */
.rq-error { margin-top: 6px; font-size: 13px; color: #b42318; }
.rq-alert { margin-top: 8px; padding: 12px 14px; border-radius: 10px; background: #fdecea; color: #8a1f18; font-size: 14px; }
.rq-alert__link { margin-left: 8px; text-decoration: underline; font-weight: 600; }
.rq-nav { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 22px; padding-top: 18px; border-top: 1px solid #eceff1; }
.rq-nav__spacer { flex: 1; }
.rq-nav__hint { font-size: 12.5px; color: #8a9197; }
.rq-nav .btn:disabled { opacity: 0.45; cursor: not-allowed; }
@media (max-width: 640px) { .rq-nav__hint { width: 100%; order: -1; } }
.rq-whatsapp { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: #3c474e; }
.rq-whatsapp i { color: #25d366; font-size: 18px; }
.rq-whatsapp:hover { color: #bb976d; }

/* Success */
.rq-success { text-align: center; padding: 44px 28px; }
.rq-success__icon { width: 60px; height: 60px; margin: 0 auto 16px; border-radius: 999px; background: #bb976d; color: #fff; display: flex; align-items: center; justify-content: center; }
.rq-success__title { font-size: 26px; font-weight: 600; margin: 0 0 6px; }
.rq-success__ref { color: #3c474e; margin: 0 0 14px; }
.rq-success__text { max-width: 540px; margin: 0 auto 22px; color: #3c474e; line-height: 1.6; }
.rq-actions { display: flex; gap: 12px; flex-wrap: wrap; }
.rq-actions--center { justify-content: center; }
.rq-success__hint { margin-top: 18px; font-size: 13px; color: #8a9197; }

@media (max-width: 640px) { .rq-card { padding: 18px; } .rq-success { padding: 32px 18px; } }
</style>

<style>
/* Dark mode — the .dark class sits on <html>, outside the scoped block's reach. */
.dark .rq-card { background: #1e2a35; border-color: #3c474e; }
.dark .rq-step__title, .dark .rq-label, .dark .rq-review dd, .dark .rq-contact-card__name, .dark .rq-success__title, .dark .rq-review__title, .dark .rq-details__summary, .dark .rq-product__name { color: #fff; }
.dark .rq-input, .dark .rq-qty { background: #172430; border-color: #3c474e; color: #fff; }
.dark .rq-qty__btn { background: #22313d; color: #fff; }
.dark .rq-qty__input { background: #172430; color: #fff; }
.dark .rq-type { border-color: #3c474e; color: #cfd4d8; }
.dark .rq-type.is-selected { background: rgba(187, 151, 109, 0.16); color: #fff; }
.dark .rq-drop, .dark .rq-contact-card { background: #172430; border-color: #3c474e; }
.dark .rq-drop__text, .dark .rq-contact-card__line, .dark .rq-note, .dark .rq-success__text, .dark .rq-success__ref, .dark .rq-whatsapp, .dark .rq-check, .dark .rq-product__opts { color: #cfd4d8; }
.dark .rq-details { border-color: #3c474e; }
.dark .rq-review__chip { background: #22313d; color: #fff; }
.dark .rq-divider, .dark .rq-nav { border-color: #3c474e; background-color: transparent; }
.dark .rq-divider { background: #3c474e; }
.dark .rq-steps__btn { border-color: #3c474e; }
.dark .rq-steps__item.is-active .rq-steps__btn { color: #fff; border-color: #bb976d; }
.dark .rq-skeleton__line, .dark .rq-skeleton__cards div { background: #22313d; }
.dark .rq-alert { background: rgba(180, 35, 24, 0.18); color: #ffb4ad; }
.dark .rq-ai { border-color: rgba(187, 151, 109, 0.5); background: rgba(187, 151, 109, 0.12); }
.dark .rq-ai__title { color: #fff; }
.dark .rq-ai__text { color: #cfd4d8; }
.dark .rq-ai__q { background: #172430; border-color: #3c474e; color: #fff; }
</style>
